import { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';
import { FISCAL_MONTHS, formatMonths, parseMonths } from '../utils/period';

interface MonthMultiSelectProps {
  selectedPeriod: string;
  onChange: (selectedPeriod: string) => void;
  className?: string;
}

const getButtonLabel = (months: string[]) => {
  if (months.length === FISCAL_MONTHS.length) return 'Tous les mois';
  if (months.length > 3) return `${months.length} mois`;
  return FISCAL_MONTHS.filter(m => months.includes(m.value)).map(m => m.label).join(', ');
};

export default function MonthMultiSelect({ selectedPeriod, onChange, className = '' }: MonthMultiSelectProps) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  // Cochés dans le menu, appliqués à sa fermeture : certaines pages se démontent pendant le rechargement
  const [draft, setDraft] = useState<string[] | null>(null);
  const months = parseMonths(selectedPeriod);

  const close = () => {
    if (draft && formatMonths(draft) !== formatMonths(months)) onChange(formatMonths(draft));
    setDraft(null);
  };

  const toggleMonth = (month: string) => {
    if (!draft) return;
    setDraft(draft.includes(month) ? draft.filter(m => m !== month) : [...draft, month]);
  };

  const rect = buttonRef.current?.getBoundingClientRect();

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        aria-haspopup="true"
        aria-expanded={draft !== null}
        onClick={() => setDraft(months)}
        className={`flex items-center gap-2 ${className}`}
      >
        {getButtonLabel(months)}
        <ChevronDown size={14} className="shrink-0" />
      </button>

      {/* Rendu dans le body : les en-têtes de page rognent leur contenu */}
      {draft && rect && createPortal(
        <>
          <div className="fixed inset-0 z-[190]" onClick={close} />
          <div
            className="fixed z-[191] w-64 max-w-[calc(100vw-2rem)] bg-white border border-slate-200 rounded-xl shadow-lg p-3"
            style={{ top: rect.bottom + 6, right: Math.max(16, window.innerWidth - rect.right) }}
          >
            <div className="grid grid-cols-2 gap-1">
              {FISCAL_MONTHS.map(month => {
                const checked = draft.includes(month.value);
                return (
                  <label
                    key={month.value}
                    className="flex items-center gap-2 px-2 py-1.5 text-sm text-slate-700 rounded-lg hover:bg-slate-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={checked && draft.length === 1}
                      onChange={() => toggleMonth(month.value)}
                      className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-400"
                    />
                    {month.label}
                  </label>
                );
              })}
            </div>
            <button
              type="button"
              onClick={close}
              className="w-full mt-2 px-3 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors"
            >
              Appliquer
            </button>
          </div>
        </>,
        document.body
      )}
    </>
  );
}
